import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Pagination } from '../../components/ui/Pagination';
import { formatNumber, inputClass, inputErrorClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';

export const PAGE_SIZES = [10, 25, 50, 100];
export const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
export const openButtonClass = 'rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50';
export const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
export const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
export const formatMoment = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
export const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
export const labelOf = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
// The value of a number field, or null when it is empty
export const numberOrNull = (value: string) => (value.trim() === '' ? null : Number(value));
export const isNumber = (value: string) => value.trim() === '' || Number.isFinite(Number(value));

export interface ProductChoice {
  id: string;
  name: string;
  sku: string;
}

// Search for a product and choose it; shows the choice once made
export const ProductPicker: React.FC<{ id: string; value: ProductChoice | null; onChange: (product: ProductChoice | null) => void; disabled?: boolean; error?: boolean }> = ({ id, value, onChange, disabled, error }) => {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  const { data: found = [], isFetching } = useQuery({
    queryKey: ['part-approval', 'products', search],
    queryFn: async (): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
    enabled: search.length >= 2 && !value,
  });
  if (value) {
    return (
      <p id={id} className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900 ring-1 ring-gray-200">
        <span>{value.name} <span className="text-xs text-gray-500">{value.sku}</span></span>
        {!disabled && (
          <button type="button" onClick={() => onChange(null)} aria-label="Choose another part" className="rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700">
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </p>
    );
  }
  return (
    <>
      <input id={id} type="search" value={input} onChange={(event) => setInput(event.target.value)} placeholder="Search product or SKU" className={cn(inputClass, error && inputErrorClass)} />
      {search.length >= 2 && (
        <ul className="mt-1 max-h-40 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
          {found.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
          ) : (
            found.map((product) => (
              <li key={product.id}>
                <button type="button" onClick={() => { onChange({ id: product.id, name: product.name, sku: product.sku }); setInput(''); setSearch(''); }} className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                  <span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </>
  );
};

// "Showing a–b of N", rows per page and the pager under a table
export const TableFooter: React.FC<{ page: number; limit: number; total: number; totalPages: number; onPage: (page: number) => void; onLimit: (limit: number) => void }> = ({ page, limit, total, totalPages, onPage, onLimit }) => (
  <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
      <span>Showing {total > 0 ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, total)} of {formatNumber(total)}</span>
      <label className="flex items-center gap-2">
        Rows per page
        <select value={limit} onChange={(event) => onLimit(Number(event.target.value))} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
          {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
        </select>
      </label>
    </div>
    {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPage} />}
  </div>
);

// Search with a 300 ms pause, page and rows per page for a list
export const useListState = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  return { page, setPage, limit, setLimit: (value: number) => { setLimit(value); setPage(1); }, searchInput, setSearchInput, search };
};
